import * as WebBrowser from 'expo-web-browser';
import { Platform } from 'react-native';

/**
 * Opens a URL in the appropriate browser for the platform.
 * - Web: Opens in new tab with security attributes
 * - Native: Opens in in-app browser (Safari/Chrome)
 *
 * @param url - The URL to open
 */
export async function openReferenceUrl(url: string): Promise<void> {
  if (Platform.OS === 'web') {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    await WebBrowser.openBrowserAsync(url);
  }
}

/**
 * Compact display string for a URL when no resolved title is available.
 * Strips protocol and `www.`, returns `<host><path>`. Falls back to
 * the verbatim URL when parsing fails.
 *
 * @example
 * formatReferenceUrlForDisplay('https://www.pubmed.ncbi.nlm.nih.gov/12345/')
 *   // → 'pubmed.ncbi.nlm.nih.gov/12345'
 */
export function formatReferenceUrlForDisplay(url: string): string {
  try {
    const parsed = new URL(url);

    const host = parsed.hostname.replace(/^www\./, '');

    const path = parsed.pathname === '/' ? '' : parsed.pathname.replace(/\/$/, '');

    return `${host}${path}`;
  } catch {
    return url;
  }
}

/**
 * Hostname only — `www.` stripped — for analytics cohorting.
 * Returns `undefined` when the URL is malformed so the analytics
 * payload omits the property rather than carrying noise.
 *
 * @example
 * getReferenceUrlHost('https://www.pubmed.ncbi.nlm.nih.gov/12345')
 *   // → 'pubmed.ncbi.nlm.nih.gov'
 */
export function getReferenceUrlHost(url: string): string | undefined {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return undefined;
  }
}
